import React, { ReactNode } from 'react';
import remarkDirective from 'remark-directive';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkBreaks from 'remark-breaks';

import './index.less';

import SyntaxHighlightedContent from '../SyntaxHighlightedContent';

const MAIN_CLASS_NAME = 'markdown-content';
const TABLE_CLASS_NAME = `${MAIN_CLASS_NAME}__table`;
const TH_CLASS_NAME = `${MAIN_CLASS_NAME}__th`;
const TD_CLASS_NAME = `${MAIN_CLASS_NAME}__td`;
const PRE_CLASS_NAME = `${MAIN_CLASS_NAME}__pre`;
const CODE_CLASS_NAME = `${MAIN_CLASS_NAME}__code`;
const P_CLASS_NAME = `${MAIN_CLASS_NAME}__p`;
const UL_CLASS_NAME = `${MAIN_CLASS_NAME}__ul`;
const OL_CLASS_NAME = `${MAIN_CLASS_NAME}__ol`;
const DL_CLASS_NAME = `${MAIN_CLASS_NAME}__dl`;
const LI_CLASS_NAME = `${MAIN_CLASS_NAME}__li`;

export interface IMarkdownContentProps {
  children: string;
}

const MarkdownContent = ({ children }: IMarkdownContentProps): JSX.Element => {
  const sanitizeSchema = {
    ...defaultSchema,
    attributes: {
      ...defaultSchema.attributes,
      code: [...(defaultSchema.attributes?.code || []), ['className']],
    },
  };

  return (
    <ReactMarkdown
      components={{
        code(props: { children: ReactNode; className: string; inline: boolean }) {
          const { children, className, inline } = props;
          if (inline) {
            return <code className={CODE_CLASS_NAME}>{children}</code>;
          }
          const match = /language-(\w+)/.exec(className || '');
          const language = match ? match[1] : 'text';
          return (
            <SyntaxHighlightedContent language={language}>
              {String(children).replace(/\n$/, '')}
            </SyntaxHighlightedContent>
          );
        },
        pre: ({ children }: { children: ReactNode }) => <pre className={PRE_CLASS_NAME}>{children}</pre>,
        table: ({ children }: { children: ReactNode }) => (
          <div className={TABLE_CLASS_NAME}>
            <table>{children}</table>
          </div>
        ),
        th: ({ children }: { children: ReactNode }) => <th className={TH_CLASS_NAME}>{children}</th>,
        td: ({ children }: { children: ReactNode }) => <td className={TD_CLASS_NAME}>{children}</td>,
        p: ({ children }: { children: ReactNode }) => <p className={P_CLASS_NAME}>{children}</p>,
        ul: ({ children }: { children: ReactNode }) => <ul className={UL_CLASS_NAME}>{children}</ul>,
        ol: ({ children }: { children: ReactNode }) => <ol className={OL_CLASS_NAME}>{children}</ol>,
        dl: ({ children }: { children: ReactNode }) => <dl className={DL_CLASS_NAME}>{children}</dl>,
        li: ({ children }: { children: ReactNode }) => <li className={LI_CLASS_NAME}>{children}</li>,
      }}
      linkTarget="_blank"
      rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
      remarkPlugins={[remarkBreaks, remarkGfm, remarkDirective]}
    >
      {children}
    </ReactMarkdown>
  );
};

export default MarkdownContent;
